import {
    AlertModal,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    Modal,
    showToast,
    TextBlock,
} from "@bcc-ui/ui-components";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";

import style from "./asset-view-kebab-menu.scss";
import { CreateAssetCustomViewSubpage } from "components/asset-management-system/custom-views/CreateAssetCustomViewSubpage";
import { MoveToFolderModal } from "components/asset-management-system/custom-views/MoveToFolderModal";
import { subpageContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { findViewByUuid } from "components/navigation/navigation-panel/sub-navigation-panel/contents/assets-sub-menu/assetSubMenuUtils";
import { Result } from "components/reports/ManageEditReportView";
import { ASSET_OVERVIEW_ROUTE, enableCustomAssetViewRoutes } from "components/router/Routes";
import DeleteItemStatusDialog from "components/status-dialog/DeleteItemStatusDialog";
import { AssetCustomView } from "domain/asset-management/assets";
import {
    AUTH_ASSET_VIEW,
    AUTH_CUSTOM_ASSET_VIEW_CREATE,
    AUTH_CUSTOM_ASSET_VIEW_DELETE,
    AUTH_CUSTOM_ASSET_VIEW_EDIT,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
} from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setViews } from "store/assetViews";
import { logger } from "utils/logging";

const DELETE_CONFIRMATION_WORD = "DELETE";
const DUPLICATE_NAME_SUFFIX = "_duplicate";
const ON_VIEW_DELETE_DELAY_MILLISECONDS = 3_000;

const connector = connect((state: StoreState) => ({
    user: state.userReducer.user,
    theme: state.themeReducer.theme,
}));

interface Props {
    name: string;
    viewDetails: AssetCustomView;
    onViewDelete: () => void;
    noHoverStyle?: boolean;
    kebabSize?: "Sm" | "Md";
    kebabColor?: string;
    isOpenedFromNavigation?: boolean;
    isKebabOpened?: (isOpen: boolean) => void;
}

const AssetViewKebabMenu = (props: Props & ConnectedProps<typeof connector>) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const assetViews = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const latestViewDetails = findViewByUuid(assetViews ?? [], props.viewDetails.uuid) ?? props.viewDetails;
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [deleteAssetViewModalVisible, setDeleteAssetViewModalVisible] = React.useState(false);
    const [assetNotDeleted, setAssetNotDeleted] = React.useState<boolean>(false);
    const [moveModalVisible, setMoveModalVisible] = React.useState(false);
    const [availableFolders, setAvailableFolders] = React.useState<AssetCustomView[]>([]);
    const subpage = React.useContext(subpageContext);
    const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);
    const [errorModalMessage, setErrorModalMessage] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const isMobileResolution = window.innerWidth < Breakpoint.MD;
    const context = React.useContext(subpageContext);

    const mainNavigationStyles = {
        kebabIconColor: "--app-subNavigationPanel-iconColor",
        iconButton: style.mainNavigationIconButton,
        dropdownContent: style.mainNavigationDropdownContent,
    };

    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleEdit = () => {
        subpage?.setSubpage({
            content: (
                <CreateAssetCustomViewSubpage
                    viewDetails={props.viewDetails}
                    folders={(assetViews ?? []).filter((view) => view.folder)}
                />
            ),
            isModal: false,
        });

        if (isMobileResolution && props.isOpenedFromNavigation) {
            context?.closeSubNavigationPanel();
        }
    };

    const handleArchive = async () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsLoading(true);
        try {
            await assetViewService.archiveAssetView(props.viewDetails.uuid, abortController);
            showToast(t("AssetManagement.archiveGroup.successMessage"), "success");
            window.location.replace(ASSET_OVERVIEW_ROUTE.path);
        } catch (error) {
            if (!abortController.signal.aborted) {
                logger.debug("Failed to archive asset view:", error);
                setErrorModalMessage(t("AssetManagement.archiveGroup.failureMessage"));
                setIsErrorModalOpen(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleExport = () => {
        const exportData = JSON.stringify(
            {
                view: {
                    name: props.viewDetails.name,
                    columns: props.viewDetails.columns,
                    filters: props.viewDetails.filters,
                },
            },
            null,
            4
        );

        const blob = new Blob([exportData], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${props.viewDetails.name}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDuplicate = () => {
        subpage?.setSubpage({
            content: (
                <CreateAssetCustomViewSubpage
                    viewDetails={{
                        ...props.viewDetails,
                        uuid: "",
                        name: props.viewDetails.name + DUPLICATE_NAME_SUFFIX,
                    }}
                    isDuplicate
                    folders={(assetViews ?? []).filter((view) => view.folder)}
                />
            ),
            isModal: false,
        });

        if (isMobileResolution && props.isOpenedFromNavigation) {
            context?.closeSubNavigationPanel();
        }
    };

    const refreshAssetViews = () =>
        assetViewService
            .fetchAssetRelatedViews()
            .then((views) => {
                enableCustomAssetViewRoutes(views);
                dispatch(setViews(views));
            })
            .catch((err) => logger.error("Failed to refresh asset views:", err));

    const handleDelete = () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        assetViewService
            .removeAssetsFromGroup(props.viewDetails.uuid, [], abortController, true)
            .then(() => assetViewService.deleteAssetView(props.viewDetails.uuid, abortController))
            .then(() => {
                showToast(
                    t("AssetManagement.deleteAssetView.successMessage", { assetViewName: props.viewDetails.name }),
                    "success"
                );
                setDeleteAssetViewModalVisible(false);
                setTimeout(() => props.onViewDelete(), ON_VIEW_DELETE_DELAY_MILLISECONDS);
                refreshAssetViews();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setDeleteAssetViewModalVisible(false);
                    setAssetNotDeleted(true);
                    setResult({
                        title: t("AssetManagement.deleteAssetView.assetViewNotDeletedTitle"),
                        message: t("AssetManagement.deleteAssetView.failureMessage"),
                    });
                }
            });
    };

    const fetchFolders = async () => {
        try {
            const views = await assetViewService.fetchAssetRelatedViews();
            setAvailableFolders(views.filter((view) => view.folder));
        } catch (error) {
            console.error("Failed to fetch folders:", error);
        }
    };

    const handleMove = async (targetFolderUuid: string | null) => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsLoading(true);
        try {
            await assetViewService.moveAssetCustomView(props.viewDetails.uuid, targetFolderUuid, abortController);
            showToast(t("AssetManagement.moveToFolder.successMessage"), "success");
            await refreshAssetViews();
        } catch (error) {
            if (!abortController.signal.aborted) {
                logger.debug("Failed to move asset view to folder:", error);
                setErrorModalMessage(t("AssetManagement.moveToFolder.failureMessage"));
                setIsErrorModalOpen(true);
            }
        } finally {
            setIsLoading(false);
            setMoveModalVisible(false);
        }
    };

    const handleMoveClick = async () => {
        await fetchFolders();
        setMoveModalVisible(true);
    };

    const handleDetachFromFolder = () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsLoading(true);
        assetViewService
            .detachFromFolder(abortController, props.viewDetails.uuid)
            .then(() => {
                showToast(t("AssetManagement.detachFromFolder.successMessage"), "success");
                return refreshAssetViews();
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.debug("Failed to remove asset view from folder:", error);
                    setErrorModalMessage(t("AssetManagement.detachFromFolder.failureMessage"));
                    setIsErrorModalOpen(true);
                }
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Box onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
                position={props.isOpenedFromNavigation && isMobileResolution ? "bottom-end" : "bottom-start"}
                dropdownOffset="Xxs"
                onDropdownOpenChange={props.isKebabOpened}
                closeOnSelection
                flipOptions={{
                    fallbackPlacements:
                        props.isOpenedFromNavigation && isMobileResolution ? ["top-end"] : ["top-start"],
                }}
            >
                <DropdownTrigger>
                    <IconButton
                        size={props.kebabSize ?? "Sm"}
                        noHoverStyle={props.noHoverStyle}
                        className={props.isOpenedFromNavigation ? mainNavigationStyles.iconButton : undefined}
                        onClick={(e) => e.preventDefault()}
                    >
                        <Kebab
                            color={
                                props.isOpenedFromNavigation
                                    ? mainNavigationStyles.kebabIconColor
                                    : props.kebabColor
                                      ? props.kebabColor
                                      : undefined
                            }
                        />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent
                    className={props.isOpenedFromNavigation ? mainNavigationStyles.dropdownContent : undefined}
                >
                    {userSessionService.hasCustomViewPermission(
                        props.user?.featureLicenses,
                        [AUTH_CUSTOM_ASSET_VIEW_EDIT],
                        "assets",
                        props.user
                    ) && <DropdownMenuItem onClick={handleEdit}>{t("Common.edit")}</DropdownMenuItem>}
                    <DropdownMenuItem onClick={handleExport}>{t("Common.export")}</DropdownMenuItem>
                    {!props.viewDetails.folder &&
                        userSessionService.hasCustomViewPermission(
                            props.user?.featureLicenses,
                            [AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW],
                            "assets",
                            props.user
                        ) && (
                            <DropdownMenuItem onClick={handleMoveClick}>
                                {t("AssetManagement.moveToFolder.menuItem")}
                            </DropdownMenuItem>
                        )}
                    {!latestViewDetails.folder &&
                        latestViewDetails.parentUuid !== undefined &&
                        userSessionService.hasCustomViewPermission(
                            props.user?.featureLicenses,
                            [AUTH_CUSTOM_ASSET_VIEW_VIEW],
                            "assets",
                            props.user
                        ) && (
                            <DropdownMenuItem onClick={handleDetachFromFolder}>
                                {t("AssetManagement.detachFromFolder.title")}
                            </DropdownMenuItem>
                        )}
                    {!props.viewDetails.folder &&
                        !props.viewDetails.isArchived &&
                        userSessionService.hasCustomViewPermission(
                            props.user?.featureLicenses,
                            [AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_EDIT],
                            "assets",
                            props.user
                        ) && (
                            <DropdownMenuItem onClick={handleArchive}>
                                {t("AssetManagement.archiveGroup.menuItem")}
                            </DropdownMenuItem>
                        )}
                    {userSessionService.hasCustomViewPermission(
                        props.user?.featureLicenses,
                        [AUTH_CUSTOM_ASSET_VIEW_CREATE, AUTH_CUSTOM_ASSET_VIEW_VIEW],
                        "assets",
                        props.user
                    ) && (
                        <DropdownMenuItem hidden={false} onClick={handleDuplicate}>
                            {t("Common.duplicate")}
                        </DropdownMenuItem>
                    )}
                    {userSessionService.hasCustomViewPermission(
                        props.user?.featureLicenses,
                        [AUTH_CUSTOM_ASSET_VIEW_DELETE],
                        "assets",
                        props.user
                    ) && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={(e) => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.ASSET_VIEW_MANAGEMENT,
                                        action: Action.REMOVE_ASSET_VIEW,
                                    });
                                    setDeleteAssetViewModalVisible(true);
                                    e.preventDefault();
                                }}
                                errorStyle
                            >
                                {t("Common.delete")}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>
            <DeleteItemStatusDialog
                title={result.title}
                modalDisplayed={assetNotDeleted}
                setModalDisplayed={() => setAssetNotDeleted(false)}
                handleCommand={handleDelete}
                ditchLink={true}
            />
            <DeleteModal
                title={t("AssetManagement.deleteAssetView.title")}
                isOpen={deleteAssetViewModalVisible}
                hideModal={() => setDeleteAssetViewModalVisible(false)}
                onDelete={handleDelete}
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_WORD,
                    deleteButtonText: t("AssetManagement.deleteAssetView.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                dataType={"DELETE_ASSET_VIEW"}
            >
                <Trans
                    i18nKey="AssetManagement.deleteAssetView.introductionMessage"
                    values={{ assetViewName: props.viewDetails.name }}
                    components={{ p: <p /> }}
                />
            </DeleteModal>
            <MoveToFolderModal
                isOpen={moveModalVisible}
                onClose={() => setMoveModalVisible(false)}
                onMove={handleMove}
                availableFolders={availableFolders}
                currentViewUuid={props.viewDetails.uuid}
                viewName={props.viewDetails.name}
            />
            <AlertModal
                title={t("Common.failure")}
                variant={"Error"}
                isOpen={isErrorModalOpen}
                hideModal={() => setIsErrorModalOpen(false)}
                buttons={
                    <Button onClick={() => setIsErrorModalOpen(false)} variant={"Primary"}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{errorModalMessage}</TextBlock>
            </AlertModal>
            <Modal
                isOpen={isLoading}
                hideModal={() => {}}
                isLoading={true}
                loadingText={t("Common.pleaseWait")}
                hideCloseIcon={true}
            >
                <LoadingIndicator />
            </Modal>
        </Box>
    );
};

export default connector(AssetViewKebabMenu);
